import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Link, LogSentryException, TextField } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";

import useNavigation from "../../hooks/navigation";
import { useAuthMethod } from "../../hooks/useAuth";
import usePrevious from "../../hooks/usePrevious";
import { useSimulateTabFocusInApp } from "../../hooks/useSimulateTabFocusInApp";
import useTranslation from "../../hooks/useTranslation";
import { useEmailStore } from "../../stores/emailStore";
import { AuthError, AuthMethod, SearchParam } from "../../types";
import { FormWrapper } from "../FormWrapper/FormWrapper";
import Heading from "../Heading/Heading";
import ReCAPTCHA, { type ReCAPTCHAAPI } from "../ReCAPTCHA/ReCAPTCHA";

import style from "./SignIn.module.css";

interface SignInProps {
  submit: (email: string, password: string, recaptcha?: string) => void;

  inProgress: boolean;
  hideRegistration?: boolean;
  loginError?: AuthError | null;
  isAuthHandoffFailed?: boolean;
  recaptchaLoaded?: boolean; // only for testing
}

const SignIn = ({
  submit,
  inProgress,
  hideRegistration,
  loginError,
  isAuthHandoffFailed,
  recaptchaLoaded,
}: SignInProps) => {
  useSimulateTabFocusInApp();

  const [isCaptchaEnabled, setIsCaptchaEnabled] = useState(false);
  const { t, m } = useTranslation();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const recaptchaRef = useRef<ReCAPTCHAAPI>(null);

  const errorMessage = useMemo(
    () => ({
      InvalidEmail: t(m.ErrorMessage.InvalidEmail),
      InvalidPassword: t(m.ErrorMessage.InvalidPassword),
      InvalidEmailOrPassword: t(m.ErrorMessage.InvalidEmailOrPassword),
      RateLimit: t(m.ErrorMessage.RateLimitError),
      Unknown: t(m.ErrorMessage.Unknown),
      AuthMethod: t(m.ErrorMessage.Unknown),
      CaptchaError: t(m.ErrorMessage.CaptchaError),
    }),
    [t, m]
  );

  const schema = z.object({
    email: z.string().min(1, errorMessage.InvalidEmail),
    password: z.string().optional(),
  });

  type SignInFormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    setValue,
    reset,
    trigger,
    resetField,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<SignInFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: useEmailStore.getState().email,
      password: "",
    },
    reValidateMode: "onSubmit",
    resetOptions: {
      keepDirty: false,
      keepTouched: false,
      keepErrors: false,
    },
    shouldUnregister: true,
  });
  const {
    mutateAsync: mutateAuthMethod,
    reset: resetAuthMethod,
    data: authMethodData,

    isSuccess: isAuthMethodSuccess,
    isPending: isAuthMethodPending,
  } = useAuthMethod();

  const email = watch("email");
  const previousEmail = usePrevious(email);
  useEffect(() => {
    if (email !== previousEmail) {
      resetAuthMethod();
      clearErrors();
      resetField("password");
    }
  }, [clearErrors, email, previousEmail, reset, resetAuthMethod, resetField, setValue, trigger]);

  useEffect(() => {
    if (isAuthMethodSuccess && authMethodData?.method === AuthMethod.Password) {
      setTimeout(() => setFocus("password"));
    }
  }, [isAuthMethodSuccess, authMethodData?.method, setFocus]);

  useEffect(() => {
    if (authMethodData?.method === AuthMethod.SSO && authMethodData.url) {
      window.location.replace(authMethodData.url);
    } else if (authMethodData?.method === AuthMethod.SSO && !authMethodData.url) {
      setError("root", { message: errorMessage.AuthMethod });
    }
  }, [authMethodData, errorMessage.AuthMethod, setError]);

  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(recaptchaLoaded);
  const onSubmit = useCallback(
    async (data: SignInFormData) => {
      const { email, password } = data;

      if (!isAuthMethodSuccess) {
        try {
          await mutateAuthMethod({
            email,
            redirectURL: searchParams.get(SearchParam.RedirectUrl),
          });
        } catch (error) {
          setError("root", { message: errorMessage.AuthMethod });
        }
        return;
      }
      if (!password) {
        setError("password", { message: errorMessage.InvalidPassword });
        return;
      }

      if (!isCaptchaEnabled) {
        submit(email, password);
        return;
      }

      if (!recaptchaRef.current) {
        setError("root", { message: errorMessage.CaptchaError });
        return;
      }

      if (isRecaptchaLoaded) {
        try {
          const recaptchaValue = await recaptchaRef.current.execute();

          if (recaptchaValue === "cancel") {
            return;
          }

          if (!recaptchaValue) {
            setError("root", { message: errorMessage.CaptchaError });
            return;
          }

          submit(email, password, recaptchaValue);
        } catch (error) {
          setError("root", { message: errorMessage.CaptchaError });
          LogSentryException({ messagePrefix: "Captcha error", error });
        }
      }
    },
    [
      isAuthMethodSuccess,
      isCaptchaEnabled,
      isRecaptchaLoaded,
      mutateAuthMethod,
      searchParams,
      setError,
      errorMessage.AuthMethod,
      errorMessage.InvalidPassword,
      errorMessage.CaptchaError,
      submit,
    ]
  );

  useEffect(() => {
    if (
      isCaptchaEnabled &&
      recaptchaRef.current !== null &&
      loginError === AuthError.MissingCaptcha
    ) {
      handleSubmit(onSubmit)();
    }
  }, [handleSubmit, isCaptchaEnabled, loginError, onSubmit]);

  useEffect(() => {
    switch (loginError) {
      case AuthError.InvalidEmailPassword:
        setError("password", { message: errorMessage.InvalidEmailOrPassword });
        setError("email", { message: "" });
        break;
      case AuthError.RateLimit:
        setError("root", { message: errorMessage.RateLimit });
        break;
      case AuthError.MissingCaptcha:
        setIsCaptchaEnabled(true);
        break;
      case AuthError.Unknown:
        setError("root", { message: errorMessage.Unknown });
        setError("email", { message: "" });
        setError("password", { message: "" });
        break;
    }
  }, [loginError, errorMessage, isRecaptchaLoaded, setError]);

  const isLoading = inProgress || isSubmitting || isAuthMethodPending;

  const getErrorMessage = (field: { message?: string } | undefined) =>
    field?.message
      ? errorMessage[field.message as keyof typeof errorMessage] || field.message
      : undefined;

  const emailErrorMessage = getErrorMessage(errors.email);
  const passwordErrorMessage = getErrorMessage(errors.password);
  const rootErrorMessage = getErrorMessage(errors.root);

  const emailErrorHighlight = Boolean(errors.email);
  const passwordErrorHighlight = Boolean(errors.password);

  return (
    <FormWrapper className={style.signIn}>
      <div className={style.signInForm}>
        <Heading type="h1">{t(m.General.Login)}</Heading>
        {!hideRegistration && !isAuthHandoffFailed && (
          <div className={style.createAccount}>
            {t(m.Login.DontHaveAccount)}{" "}
            <Link aria-label="signup" onClick={() => navigation.goToSignUp({ email })}>
              {t(m.Login.SignUp)}
            </Link>
          </div>
        )}
        {isAuthHandoffFailed && (
          <div className={style.topErrorMessage} aria-label="error">
            There was an error logging you in. Close this window and try again, or log in below.
          </div>
        )}
        {rootErrorMessage && (
          <div className={style.topErrorMessage} aria-label="error">
            {rootErrorMessage}
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className={style.inputContainer} noValidate>
          <fieldset disabled={isLoading} className={style.fieldsetContainer}>
            <TextField
              autoFocus
              errorMessage={emailErrorMessage}
              aria-label="email"
              type="email"
              placeholder={t(m.General.Email)}
              error={emailErrorHighlight}
              tabIndex={isLoading ? -1 : 0}
              {...register("email")}
            />
            <div
              className={classNames(
                style.passwordContainer,
                authMethodData?.method !== AuthMethod.Password && style.hidden
              )}
            >
              <TextField
                errorMessage={passwordErrorMessage}
                aria-label="password"
                type="password"
                placeholder={t(m.General.Password)}
                autoComplete="current-password"
                error={passwordErrorHighlight}
                disabled={!isAuthMethodSuccess}
                tabIndex={isAuthMethodSuccess ? 0 : -1}
                {...register("password")}
              />
              <Link className={style.formLink} onClick={() => navigation.goToPasswordReset(email)}>
                {t(m.Login.ForgotPassword)}
              </Link>
            </div>
          </fieldset>
          {isCaptchaEnabled && (
            <ReCAPTCHA
              ref={recaptchaRef}
              onLoad={() => setIsRecaptchaLoaded(true)}
              onError={() => setError("root", { message: errorMessage.CaptchaError })}
            />
          )}
          <Button
            tabIndex={0}
            type="submit"
            loading={isLoading}
            aria-label={isAuthMethodSuccess ? "login" : "continue"}
          >
            <span>{isAuthMethodSuccess ? t(m.General.Login) : t(m.General.Continue)}</span>
          </Button>
        </form>
      </div>
    </FormWrapper>
  );
};

export default SignIn;
